/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import Swal from 'sweetalert2'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import { KTSVG } from '../../_metronic/helpers'
import Error from '../components/Error500'
import GetPagePermission from '../components/GetPagePermission'
import PrintFieldPicker, { ChosenField } from '../components/PrintFieldPicker'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

/**
 * Project wise print copy design. Settings keys must match backend PrintLayout::RULES,
 * the columns a document offers come from backend PrintColumns.
 * Margins are mm and sizes are px, exactly as the PDF template reads them.
 */
interface PrintSettings {
	usePad: boolean
	marginTop: number
	marginRight: number
	marginBottom: number
	marginLeft: number
	fontFamily: string
	fontSize: number
	tableFontSize: number
	textColor: string
	showTitle: boolean
	titleText: string
	titleAlign: 'left' | 'center' | 'right'
	titleSize: number
	titleColor: string
	titleUnderline: boolean
	borderColor: string
	headerBg: string
	headerColor: string
	cellPadding: number
	accentColor: string
	customColumns: boolean
	columns: ChosenField[]
	customInfo: boolean
	infoLeft: ChosenField[]
	infoRight: ChosenField[]
	customSummary: boolean
	summary: ChosenField[]
	showFreeItems: boolean
	freeItemsTitle: string
	customFreeColumns: boolean
	freeColumns: ChosenField[]
	showSignatures: boolean
	signatureLabels: string[]
	signatureGap: number
	footerNote: string
}

interface AvailableColumn {
	key: string
	label: string
	align: 'left' | 'center' | 'right'
	sample: string
}

interface AvailableInfoField {
	key: string
	label: string
	sample: string
	bold: boolean
}

interface DocumentType {
	documentType: string
	label: string
	singleLine: boolean
	defaults: PrintSettings
	columns: AvailableColumn[]
	freeColumns: AvailableColumn[]
	infoFields: AvailableInfoField[]
	summaryLines: { key: string; label: string; sample: string }[]
}

interface ProjectDocument {
	documentType: string
	label: string
	isCustomized: boolean
	padUrl: string | null
	settings: PrintSettings
}

interface ProjectOption {
	idProjects: number
	projectName: string
	projectShortName: string | null
}

const A4_WIDTH_PX = (210 * 96) / 25.4
const A4_RATIO = 297 / 210
const MAX_SIGNATURES = 6

// Browser stand-ins for the PDF fonts, for the live preview only.
const PREVIEW_FONTS: Record<string, string> = {
	times: '"Times New Roman", Times, serif',
	helvetica: 'Helvetica, Arial, sans-serif',
	courier: '"Courier New", Courier, monospace',
	'dejavu sans': '"DejaVu Sans", Verdana, sans-serif',
}

const authHeader = () => ({
	Accept: 'application/json',
	Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('MyToken') as string),
})

const showErrors = (resp: any) => {
	const errorsMessage = resp?.errors ? validationErrorMsg(resp.errors).join('<br>') : resp?.message || 'Something went wrong'
	Swal.fire({ icon: 'error', title: 'Error', html: errorsMessage })
}

const readImageSize = (file: File) =>
	new Promise<{ width: number; height: number }>((resolve, reject) => {
		const image = new Image()
		image.onload = () => {
			resolve({ width: image.naturalWidth, height: image.naturalHeight })
			URL.revokeObjectURL(image.src)
		}
		image.onerror = reject
		image.src = URL.createObjectURL(file)
	})

function PrintDesigns() {
	const hasPermission = GetPagePermission('/print-designs')

	const [loading, setLoading] = useState(true)
	const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
	const [fontFamilies, setFontFamilies] = useState<{ value: string; label: string }[]>([])
	const [projects, setProjects] = useState<ProjectOption[]>([])
	const [idProjects, setIdProjects] = useState<number | ''>('')

	const [projectPadUrl, setProjectPadUrl] = useState<string | null>(null)
	const [documents, setDocuments] = useState<ProjectDocument[]>([])
	const [drafts, setDrafts] = useState<Record<string, PrintSettings>>({})
	const [dirtyTypes, setDirtyTypes] = useState<string[]>([])
	const [activeType, setActiveType] = useState('')

	const [applyToAll, setApplyToAll] = useState(false)
	const [showGuides, setShowGuides] = useState(true)
	const [busy, setBusy] = useState(false)
	const [previewScale, setPreviewScale] = useState(0.5)
	const previewWrapRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'print-designs/meta', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setDocumentTypes(resp.documentTypes || [])
				setFontFamilies(resp.fontFamilies || [])
				setProjects(resp.projects || [])
				if (resp.documentTypes?.length) {
					setActiveType(resp.documentTypes[0].documentType)
				}
				if (resp.projects?.length) {
					setIdProjects(resp.projects[0].idProjects)
				} else {
					setLoading(false)
				}
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
				setLoading(false)
			})
	}, [])

	useEffect(() => {
		if (idProjects !== '') {
			getProjectDesigns(idProjects)
		}
	}, [idProjects])

	useEffect(() => {
		const updateScale = () => {
			if (previewWrapRef.current) {
				setPreviewScale(previewWrapRef.current.clientWidth / A4_WIDTH_PX)
			}
		}
		updateScale()
		window.addEventListener('resize', updateScale)
		return () => window.removeEventListener('resize', updateScale)
	}, [loading, hasPermission])

	const getProjectDesigns = (id: number) => {
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'print-designs/' + id, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const rows: ProjectDocument[] = resp.documents || []
				const nextDrafts: Record<string, PrintSettings> = {}
				rows.forEach((row) => {
					nextDrafts[row.documentType] = row.settings
				})
				setProjectPadUrl(resp.padUrl || null)
				setDocuments(rows)
				setDrafts(nextDrafts)
				setDirtyTypes([])
				setLoading(false)
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
				setLoading(false)
			})
	}

	const activeDocument = documents.find((row) => row.documentType === activeType)
	const settings = drafts[activeType]
	const activePadUrl = activeDocument?.padUrl || projectPadUrl
	const isDirty = dirtyTypes.includes(activeType)
	const availableColumns = documentTypes.find((row) => row.documentType === activeType)?.columns || []
	const columnOf = (key: string) => availableColumns.find((column) => column.key === key)
	const availableFreeColumns = documentTypes.find((row) => row.documentType === activeType)?.freeColumns || []
	const availableSummaryLines = documentTypes.find((row) => row.documentType === activeType)?.summaryLines || []
	const availableInfoFields = documentTypes.find((row) => row.documentType === activeType)?.infoFields || []
	// A document whose item table is always one line (Inter BU Invoice 2)
	const singleLine = documentTypes.find((row) => row.documentType === activeType)?.singleLine || false

	const setSetting = <K extends keyof PrintSettings>(key: K, value: PrintSettings[K]) => {
		setSettings({ [key]: value } as unknown as Partial<PrintSettings>)
	}

	const setSettings = (changes: Partial<PrintSettings>) => {
		setDrafts({ ...drafts, [activeType]: { ...drafts[activeType], ...changes } })
		if (!dirtyTypes.includes(activeType)) {
			setDirtyTypes([...dirtyTypes, activeType])
		}
	}

	const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const nextId = Number(e.target.value)
		if (dirtyTypes.length === 0) {
			setIdProjects(nextId)
			return
		}

		Swal.fire({
			title: 'Discard unsaved changes?',
			text: 'This project has print design changes that are not saved.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Discard',
			cancelButtonText: 'Keep editing',
			confirmButtonColor: '#ec008c',
		}).then((result) => {
			if (result.value) {
				setIdProjects(nextId)
			}
		})
	}

	const handleSave = (evt: React.FormEvent) => {
		evt.preventDefault()
		if (!settings || idProjects === '') {
			return
		}

		setBusy(true)
		fetch(
			process.env.REACT_APP_API_URL + 'print-designs/' + idProjects + '/' + activeType,
			postRequestOptions({ settings, applyToAll })
		)
			.then((resp) => resp.json())
			.then((resp) => {
				setBusy(false)
				if (resp.success === true) {
					Swal.fire({ icon: 'success', title: 'Success', text: resp.successMessage })
					if (applyToAll) {
						setApplyToAll(false)
						getProjectDesigns(idProjects)
					} else {
						// Only this document was saved: keep unsaved edits of the other documents.
						applySavedSettings(resp.settings, true)
					}
				} else {
					showErrors(resp)
				}
			})
			.catch((error) => {
				setBusy(false)
				console.log(error, 'catch the hoop')
			})
	}

	const handleReset = () => {
		Swal.fire({
			title: 'Reset to default?',
			text: `The ${activeDocument?.label} design of this project will go back to the default design.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Reset',
			cancelButtonText: 'Cancel',
			confirmButtonColor: '#ec008c',
		}).then((result) => {
			if (!result.value || idProjects === '') {
				return
			}

			fetch(
				process.env.REACT_APP_API_URL + 'print-designs/' + idProjects + '/' + activeType + '/reset',
				postRequestOptions({})
			)
				.then((resp) => resp.json())
				.then((resp) => {
					if (resp.success === true) {
						const defaults = documentTypes.find((row) => row.documentType === activeType)?.defaults
						if (defaults) {
							applySavedSettings(defaults, false)
						}
					} else {
						showErrors(resp)
					}
				})
				.catch((error) => {
					console.log(error, 'catch the hoop')
				})
		})
	}

	const applySavedSettings = (saved: PrintSettings, isCustomized: boolean) => {
		setDrafts({ ...drafts, [activeType]: saved })
		setDocuments(documents.map((row) => (row.documentType === activeType ? { ...row, settings: saved, isCustomized } : row)))
		setDirtyTypes(dirtyTypes.filter((type) => type !== activeType))
	}

	const handlePreviewPdf = (multiPage: boolean) => {
		// Opened before the request so the browser does not treat it as a popup.
		const previewWindow = window.open('', '_blank')

		fetch(
			process.env.REACT_APP_API_URL + 'print-designs/' + idProjects + '/' + activeType + '/preview',
			{
				method: 'POST',
				headers: { ...authHeader(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ settings, multiPage }),
			}
		)
			.then((resp) => {
				if (!resp.ok) {
					throw new window.Error('Preview failed')
				}
				return resp.blob()
			})
			.then((blob) => {
				const url = URL.createObjectURL(blob)
				if (previewWindow) {
					previewWindow.location.href = url
				} else {
					window.location.href = url
				}
			})
			.catch((error) => {
				previewWindow?.close()
				console.log(error, 'catch the hoop')
				Swal.fire({ icon: 'error', title: 'Error', text: 'Could not create the preview PDF.' })
			})
	}

	const uploadPad = async (file: File, documentType: string | null) => {
		if (!['image/png', 'image/jpeg'].includes(file.type)) {
			Swal.fire({ icon: 'error', title: 'File type not supported', text: 'Upload the pad as a JPG or PNG image of the full A4 page.' })
			return
		}
		if (file.size > 5 * 1024 * 1024) {
			Swal.fire({ icon: 'error', title: 'File too large', text: 'The pad image may not be larger than 5 MB.' })
			return
		}

		try {
			const { width, height } = await readImageSize(file)
			const warnings: string[] = []
			if (Math.abs(height / width - A4_RATIO) > 0.03) {
				warnings.push('This image is not A4 shaped (210 × 297 mm). It will be stretched to the page width and may not line up.')
			}
			if (width < 1240) {
				warnings.push(`This image is only ${width} × ${height} px and may print blurry. Use at least 1240 × 1754 px.`)
			}
			if (warnings.length > 0) {
				const confirm = await Swal.fire({
					title: 'Check the pad image',
					html: warnings.join('<br><br>'),
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Upload anyway',
					cancelButtonText: 'Cancel',
					confirmButtonColor: '#ec008c',
				})
				if (!confirm.value) {
					return
				}
			}
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Error', text: 'This image could not be read.' })
			return
		}

		const formData = new FormData()
		formData.append('pad', file)
		if (documentType) {
			formData.append('documentType', documentType)
		}

		setBusy(true)
		fetch(process.env.REACT_APP_API_URL + 'print-designs/' + idProjects + '/pad', {
			method: 'POST',
			headers: authHeader(),
			body: formData,
		})
			.then((resp) => resp.json())
			.then((resp) => {
				setBusy(false)
				if (resp.success === true) {
					setPadUrl(documentType, resp.padUrl)
				} else {
					showErrors(resp)
				}
			})
			.catch((error) => {
				setBusy(false)
				console.log(error, 'catch the hoop')
			})
	}

	const removePad = (documentType: string | null) => {
		Swal.fire({
			title: 'Remove this pad?',
			text: documentType
				? `${activeDocument?.label} will print on the project pad again.`
				: 'Every document of this project without its own pad will print without a pad.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Remove',
			cancelButtonText: 'Cancel',
			confirmButtonColor: '#ec008c',
		}).then((result) => {
			if (!result.value) {
				return
			}

			fetch(
				process.env.REACT_APP_API_URL + 'print-designs/' + idProjects + '/pad/remove',
				postRequestOptions({ documentType })
			)
				.then((resp) => resp.json())
				.then((resp) => {
					if (resp.success === true) {
						setPadUrl(documentType, null)
					} else {
						showErrors(resp)
					}
				})
				.catch((error) => {
					console.log(error, 'catch the hoop')
				})
		})
	}

	// Pads save on upload, so they are patched in place and unsaved design edits are kept.
	const setPadUrl = (documentType: string | null, padUrl: string | null) => {
		if (documentType) {
			setDocuments(documents.map((row) => (row.documentType === documentType ? { ...row, padUrl } : row)))
		} else {
			setProjectPadUrl(padUrl)
		}
	}

	const handlePadFile = (e: React.ChangeEvent<HTMLInputElement>, documentType: string | null) => {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (file) {
			uploadPad(file, documentType)
		}
	}

	const numberField = (key: keyof PrintSettings, label: string, unit: string, min: number, max: number, step = 1) => (
		<div className='col-6 col-md-3 mb-3'>
			<label className='form-label fs-8 mb-1' htmlFor={`pd-${key}`}>
				{label} <span className='text-muted'>({unit})</span>
			</label>
			<input
				type='number'
				id={`pd-${key}`}
				className='form-control form-control-sm'
				min={min}
				max={max}
				step={step}
				value={settings[key] as number}
				onChange={(e) => setSetting(key, (e.target.value === '' ? 0 : Number(e.target.value)) as never)}
			/>
		</div>
	)

	const colorField = (key: keyof PrintSettings, label: string) => (
		<div className='col-6 col-md-3 mb-3'>
			<label className='form-label fs-8 mb-1' htmlFor={`pd-${key}`}>
				{label}
			</label>
			<div className='d-flex align-items-center gap-2'>
				<input
					type='color'
					id={`pd-${key}`}
					className='form-control form-control-sm form-control-color'
					value={settings[key] as string}
					onChange={(e) => setSetting(key, e.target.value as never)}
				/>
				<span className='text-muted fs-8'>{settings[key] as string}</span>
			</div>
		</div>
	)

	const switchField = (key: keyof PrintSettings, label: string) => (
		<label className='form-check form-switch form-check-custom form-check-solid mb-3'>
			<input
				className='form-check-input'
				type='checkbox'
				checked={settings[key] as boolean}
				onChange={(e) => setSetting(key, e.target.checked as never)}
			/>
			<span className='form-check-label'>{label}</span>
		</label>
	)

	const renderColumnEditor = () => {
		if (!settings.customColumns) {
			return (
				<div className='text-muted fs-8 mb-3'>
					Automatic: {settings.columns.map((chosen) => columnOf(chosen.key)?.label).join(', ')}. Columns follow the project
					config and the document's data.
				</div>
			)
		}

		return (
			<PrintFieldPicker
				lists={[{ id: 'columns', title: 'Columns on the print', items: settings.columns, required: true }]}
				available={availableColumns}
				onChange={(listId, items) => setSetting('columns', items)}
				onMove={(changes) => setSettings(changes)}
			/>
		)
	}

	const renderSummaryEditor = () => {
		if (!settings.customSummary) {
			return (
				<div className='text-muted fs-8 mb-3'>
					Automatic: {settings.summary.map((chosen) => availableSummaryLines.find((line) => line.key === chosen.key)?.label).join(', ')}.
				</div>
			)
		}

		return (
			<>
				<PrintFieldPicker
					lists={[{ id: 'summary', title: 'Summary lines on the print', items: settings.summary, required: true }]}
					available={availableSummaryLines}
					onChange={(listId, items) => setSetting('summary', items)}
					onMove={(changes) => setSettings(changes)}
				/>
				{activeType !== 'sales_order' && activeType !== 'purchase_order' && (
					<div className='text-muted fs-8 mb-3'>
						VAT, discount, advance adjustment and return lines print only when their amount is not zero.
					</div>
				)}
			</>
		)
	}

	const renderFreeItemsEditor = () => (
		<>
			{switchField('showFreeItems', 'Print the free items table')}
			{settings.showFreeItems && (
				<>
					<div className='row'>
						<div className='col-12 col-md-6 mb-3'>
							<label className='form-label fs-8 mb-1' htmlFor='pd-freeItemsTitle'>
								Heading above the table
							</label>
							<input
								type='text'
								id='pd-freeItemsTitle'
								className='form-control form-control-sm'
								maxLength={60}
								value={settings.freeItemsTitle}
								onChange={(e) => setSetting('freeItemsTitle', e.target.value)}
							/>
						</div>
					</div>
					{switchField('customFreeColumns', 'Choose the columns of the free items table')}
					{settings.customFreeColumns ? (
						<PrintFieldPicker
							lists={[{ id: 'freeColumns', title: 'Free item columns on the print', items: settings.freeColumns, required: true }]}
							available={availableFreeColumns}
							onChange={(listId, items) => setSetting('freeColumns', items)}
							onMove={(changes) => setSettings(changes)}
						/>
					) : (
						<div className='text-muted fs-8 mb-3'>
							Automatic:{' '}
							{settings.freeColumns.map((chosen) => availableFreeColumns.find((column) => column.key === chosen.key)?.label).join(', ')}.
						</div>
					)}
				</>
			)}
			<div className='text-muted fs-8 mb-3'>The table prints only when the document has free items.</div>
		</>
	)

	const renderInfoEditor = () => {
		if (!settings.customInfo) {
			return (
				<div className='text-muted fs-8 mb-3'>
					Automatic: the document prints its own header block (customer, numbers and dates).
				</div>
			)
		}

		return (
			<PrintFieldPicker
				lists={[
					{ id: 'infoLeft', title: activeType === 'purchase_order' ? 'Supplier information' : 'Left side', items: settings.infoLeft },
					{
						id: 'infoRight',
						title: activeType === 'purchase_order' ? 'Material delivery information' : 'Right side',
						items: settings.infoRight,
					},
				]}
				available={availableInfoFields}
				onChange={(listId, items) => setSetting(listId as 'infoLeft' | 'infoRight', items)}
				onMove={(changes) => setSettings(changes)}
			/>
		)
	}

	const sectionTitle = (title: string) => <h6 className='fw-bolder border-bottom pb-2 mt-5 mb-4'>{title}</h6>

	const renderPreview = () => {
		const cell: React.CSSProperties = {
			border: `1px solid ${settings.borderColor}`,
			padding: settings.cellPadding,
			fontSize: settings.tableFontSize,
			verticalAlign: 'top',
		}
		const headCell: React.CSSProperties = {
			...cell,
			background: settings.headerBg,
			color: settings.headerColor,
			fontWeight: 'bold',
			textAlign: 'center',
		}
		const previewColumns = settings.columns
			.map((chosen) => {
				const column = columnOf(chosen.key)
				return column ? { ...column, label: chosen.label.trim() !== '' ? chosen.label : column.label } : null
			})
			.filter((column): column is AvailableColumn => column !== null)
		const previewFreeColumns =
			settings.showFreeItems && availableFreeColumns.length > 0
				? settings.freeColumns
						.map((chosen) => {
							const column = availableFreeColumns.find((item) => item.key === chosen.key)
							return column ? { ...column, label: chosen.label.trim() !== '' ? chosen.label : column.label } : null
						})
						.filter((column): column is AvailableColumn => column !== null)
				: []
		const signatureLabels = settings.showSignatures ? settings.signatureLabels.filter((label) => label.trim() !== '') : []

		return (
			<div ref={previewWrapRef} style={{ width: '100%' }}>
				<div style={{ height: A4_WIDTH_PX * A4_RATIO * previewScale, overflow: 'hidden', boxShadow: '0 0 0 1px #e4e6ef, 0 4px 14px rgba(0,0,0,.08)' }}>
					<div
						style={{
							position: 'relative',
							width: '210mm',
							height: '297mm',
							transform: `scale(${previewScale})`,
							transformOrigin: 'top left',
							background: '#fff',
							overflow: 'hidden',
							fontFamily: PREVIEW_FONTS[settings.fontFamily] || PREVIEW_FONTS.times,
							fontSize: settings.fontSize,
							lineHeight: 1.2,
							color: settings.textColor,
						}}
					>
						{settings.usePad && activePadUrl && (
							<img src={activePadUrl} alt='' style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }} />
						)}
						<div
							style={{
								position: 'absolute',
								top: `${settings.marginTop}mm`,
								right: `${settings.marginRight}mm`,
								bottom: `${settings.marginBottom}mm`,
								left: `${settings.marginLeft}mm`,
								outline: showGuides ? '1px dashed #ec008c' : 'none',
								overflow: 'hidden',
							}}
						>
							{settings.showTitle && settings.titleText !== '' && (
								<div
									style={{
										marginBottom: 10,
										textAlign: settings.titleAlign,
										fontSize: settings.titleSize,
										fontWeight: 'bold',
										color: settings.titleColor,
										textDecoration: settings.titleUnderline ? 'underline' : 'none',
									}}
								>
									{settings.titleText}
								</div>
							)}
							<div style={{ display: 'flex', fontSize: settings.tableFontSize }}>
								{[settings.infoLeft, settings.infoRight].map((side, sideIndex) => (
									<div key={sideIndex} style={{ width: sideIndex === 0 ? '54%' : '46%' }}>
										{side.map((chosen) => {
											const field = availableInfoFields.find((item) => item.key === chosen.key)
											return field ? (
												<div key={chosen.key} style={{ display: 'flex', padding: '2px 4px', fontWeight: field.bold ? 'bold' : 'normal' }}>
													<div style={{ width: '36%' }}>{chosen.label.trim() !== '' ? chosen.label : field.label}</div>
													<div style={{ width: '64%' }}>: {field.sample}</div>
												</div>
											) : null
										})}
									</div>
								))}
							</div>
							<table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
								<thead>
									<tr>
										{previewColumns.map((column) => (
											<th key={column.key} style={headCell}>
												{column.label}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{(singleLine ? [1] : [1, 2, 3, 4, 5]).map((i) => (
										<tr key={i}>
											{previewColumns.map((column) => (
												<td key={column.key} style={{ ...cell, textAlign: column.align }}>
													{column.key === 'sl' ? i : column.sample}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
							{availableSummaryLines.length > 0 && (
								<table style={{ width: '40%', marginLeft: '60%', borderCollapse: 'collapse', marginTop: 8 }}>
									<tbody>
										{settings.summary.map((chosen) => {
											const line = availableSummaryLines.find((item) => item.key === chosen.key)
											return line ? (
												<tr key={chosen.key}>
													<td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>
														{chosen.label.trim() !== '' ? chosen.label : line.label} :
													</td>
													<td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{line.sample}</td>
												</tr>
											) : null
										})}
									</tbody>
								</table>
							)}
							{previewFreeColumns.length > 0 && (
								<>
									{settings.freeItemsTitle !== '' && <div style={{ marginTop: 14, fontWeight: 'bold' }}>{settings.freeItemsTitle}</div>}
									<table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
										<thead>
											<tr>
												{previewFreeColumns.map((column) => (
													<th key={column.key} style={headCell}>
														{column.label}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{[1, 2].map((i) => (
												<tr key={i}>
													{previewFreeColumns.map((column) => (
														<td key={column.key} style={{ ...cell, textAlign: column.align }}>
															{column.key === 'sl' ? i : column.sample}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</>
							)}
							<div style={{ marginTop: 14 }}>
								<u>
									<b>TERMS OF BUSINESS:</b>
								</u>
								<br />
								1. This is a sample document to check the print design.
							</div>
							{signatureLabels.length > 0 && (
								<div style={{ display: 'flex', marginTop: `${settings.signatureGap}mm`, fontSize: settings.tableFontSize }}>
									{signatureLabels.map((label, index) => (
										<div key={index} style={{ flex: 1, padding: '0 14px', textAlign: 'center' }}>
											<div style={{ borderTop: `1px solid ${settings.textColor}`, paddingTop: 5 }}>{label}</div>
										</div>
									))}
								</div>
							)}
							{settings.footerNote !== '' && <div style={{ marginTop: 12, whiteSpace: 'pre-line' }}>{settings.footerNote}</div>}
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<>
			{hasPermission === 1 && (
				<div className='card card-custom'>
					<div className='card-header'>
						<div className='card-title'>
							<KTSVG path='/media/svg/icons/Devices/Printer.svg' className='svg-icon-1 svg-icon-primary' />
							<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
								Print Design
							</span>
						</div>
						<div className='card-toolbar'>
							<select
								className='form-select form-select-sm'
								style={{ minWidth: '240px' }}
								value={idProjects}
								onChange={handleProjectChange}
								aria-label='Project'
							>
								{projects.map((project) => (
									<option key={project.idProjects} value={project.idProjects}>
										{project.projectName}
										{project.projectShortName ? ` (${project.projectShortName})` : ''}
									</option>
								))}
							</select>
						</div>
					</div>

					<div className='card-body'>
						<p className='text-muted mb-5'>
							Upload the A4 pad of a project and design each print copy on it. Every document prints with the design saved
							here.
						</p>

						{loading && (
							<SkeletonTheme>
								<Skeleton count={12} />
							</SkeletonTheme>
						)}

						{!loading && projects.length === 0 && <div className='text-center text-muted py-8'>No projects found.</div>}

						{!loading && settings && (
							<div className='row'>
								<div className='col-lg-7'>
									<div className='border rounded p-4 mb-5'>
										<div className='d-flex align-items-center flex-wrap gap-4'>
											<div
												className='border bg-light d-flex align-items-center justify-content-center text-muted fs-9 text-center'
												style={{ width: 64, height: 90, flexShrink: 0, overflow: 'hidden' }}
											>
												{projectPadUrl ? (
													<img src={projectPadUrl} alt='Project pad' style={{ width: '100%', height: '100%' }} />
												) : (
													'No pad'
												)}
											</div>
											<div className='flex-grow-1'>
												<div className='fw-bolder'>Project pad</div>
												<div className='text-muted fs-7 mb-2'>
													A4 page image, JPG or PNG, at least 1240 × 1754 px. Used by every document of this project.
												</div>
												<label className={'btn btn-sm btn-light-primary me-2 mb-0' + (busy ? ' disabled' : '')}>
													{projectPadUrl ? 'Replace pad' : 'Upload pad'}
													<input type='file' accept='image/png,image/jpeg' hidden onChange={(e) => handlePadFile(e, null)} />
												</label>
												{projectPadUrl && (
													<button type='button' className='btn btn-sm btn-light-danger' onClick={() => removePad(null)}>
														Remove
													</button>
												)}
											</div>
										</div>
									</div>

									<ul className='nav nav-tabs nav-line-tabs mb-2 fs-7'>
										{documents.map((row) => (
											<li className='nav-item' key={row.documentType}>
												<button
													type='button'
													className={'nav-link bg-transparent' + (row.documentType === activeType ? ' active fw-bolder' : '')}
													onClick={() => setActiveType(row.documentType)}
												>
													{row.label}
													{dirtyTypes.includes(row.documentType) && <span className='text-danger'> *</span>}
													{row.isCustomized && !dirtyTypes.includes(row.documentType) && (
														<span className='bullet bullet-dot bg-success ms-2' title='Custom design saved' />
													)}
												</button>
											</li>
										))}
									</ul>

									<form onSubmit={handleSave}>
										{sectionTitle('Page')}
										{switchField('usePad', 'Print the pad on this document')}
										<div className='d-flex align-items-center flex-wrap gap-2 mb-4 fs-7'>
											<span className='text-muted'>
												{activeDocument?.padUrl ? 'This document has its own pad.' : 'This document uses the project pad.'}
											</span>
											<label className={'btn btn-sm btn-link p-0 mb-0' + (busy ? ' disabled' : '')}>
												{activeDocument?.padUrl ? 'Replace' : 'Use a different pad for this document'}
												<input type='file' accept='image/png,image/jpeg' hidden onChange={(e) => handlePadFile(e, activeType)} />
											</label>
											{activeDocument?.padUrl && (
												<button type='button' className='btn btn-sm btn-link text-danger p-0' onClick={() => removePad(activeType)}>
													Remove
												</button>
											)}
										</div>
										<div className='row'>
											{numberField('marginTop', 'Top margin', 'mm', 0, 120, 0.5)}
											{numberField('marginBottom', 'Bottom margin', 'mm', 0, 120, 0.5)}
											{numberField('marginLeft', 'Left margin', 'mm', 0, 80, 0.5)}
											{numberField('marginRight', 'Right margin', 'mm', 0, 80, 0.5)}
										</div>
										<div className='text-muted fs-8'>
											Set the top and bottom margins to the height of the pad's header and footer. Margins are kept when a
											document is downloaded without the pad, for printing on pad paper.
										</div>

										{sectionTitle('Text')}
										<div className='row'>
											<div className='col-6 col-md-3 mb-3'>
												<label className='form-label fs-8 mb-1' htmlFor='pd-fontFamily'>
													Font
												</label>
												<select
													id='pd-fontFamily'
													className='form-select form-select-sm'
													value={settings.fontFamily}
													onChange={(e) => setSetting('fontFamily', e.target.value)}
												>
													{fontFamilies.map((font) => (
														<option key={font.value} value={font.value}>
															{font.label}
														</option>
													))}
												</select>
											</div>
											{numberField('fontSize', 'Text size', 'px', 8, 20)}
											{numberField('tableFontSize', 'Table text size', 'px', 8, 20)}
											{colorField('textColor', 'Text colour')}
										</div>

										{sectionTitle('Title')}
										{activeType === 'purchase_order' ? (
											<div className='text-muted fs-8 mb-3'>
												The Purchase Order title sits in its header box, so only its text, size and colour apply.
											</div>
										) : (
											switchField('showTitle', 'Show the title')
										)}
										<div className='row'>
											<div className='col-12 col-md-6 mb-3'>
												<label className='form-label fs-8 mb-1' htmlFor='pd-titleText'>
													Title text
												</label>
												<input
													type='text'
													id='pd-titleText'
													className='form-control form-control-sm'
													maxLength={100}
													value={settings.titleText}
													onChange={(e) => setSetting('titleText', e.target.value)}
												/>
											</div>
											<div className='col-6 col-md-3 mb-3'>
												<label className='form-label fs-8 mb-1' htmlFor='pd-titleAlign'>
													Alignment
												</label>
												<select
													id='pd-titleAlign'
													className='form-select form-select-sm'
													value={settings.titleAlign}
													onChange={(e) => setSetting('titleAlign', e.target.value as PrintSettings['titleAlign'])}
												>
													<option value='left'>Left</option>
													<option value='center'>Center</option>
													<option value='right'>Right</option>
												</select>
											</div>
											{numberField('titleSize', 'Title size', 'px', 8, 36)}
											{colorField('titleColor', 'Title colour')}
											<div className='col-6 col-md-3 d-flex align-items-end'>{switchField('titleUnderline', 'Underline')}</div>
										</div>

										{sectionTitle('Table')}
										<div className='row'>
											{colorField('borderColor', 'Border colour')}
											{colorField('headerBg', 'Header background')}
											{colorField('headerColor', 'Header text')}
											{numberField('cellPadding', 'Cell padding', 'px', 0, 16)}
											{activeType === 'purchase_order' && colorField('accentColor', 'Section bar')}
										</div>

										{sectionTitle('Header details')}
										{switchField('customInfo', 'Choose the header details (customer name, address, numbers, dates)')}
										{renderInfoEditor()}

										{sectionTitle('Table columns')}
										{switchField('customColumns', 'Choose the columns of the item table')}
										{renderColumnEditor()}

										{availableSummaryLines.length > 0 && (
											<>
												{sectionTitle('Totals summary')}
												{switchField('customSummary', 'Choose the totals below the item table')}
												{renderSummaryEditor()}
											</>
										)}

										{availableFreeColumns.length > 0 && (
											<>
												{sectionTitle('Free items')}
												{renderFreeItemsEditor()}
											</>
										)}

										{sectionTitle('Signatures and footer')}
										{switchField('showSignatures', 'Show signature lines')}
										{settings.showSignatures && (
											<div className='row'>
												{settings.signatureLabels.map((label, index) => (
													<div className='col-12 col-md-6 mb-3' key={index}>
														<div className='input-group input-group-sm'>
															<input
																type='text'
																className='form-control'
																maxLength={60}
																value={label}
																aria-label={`Signature ${index + 1}`}
																onChange={(e) =>
																	setSetting(
																		'signatureLabels',
																		settings.signatureLabels.map((item, i) => (i === index ? e.target.value : item))
																	)
																}
															/>
															<button
																type='button'
																className='btn btn-light-danger'
																aria-label={`Remove signature ${index + 1}`}
																onClick={() =>
																	setSetting(
																		'signatureLabels',
																		settings.signatureLabels.filter((item, i) => i !== index)
																	)
																}
															>
																×
															</button>
														</div>
													</div>
												))}
												<div className='col-12 mb-3'>
													{settings.signatureLabels.length < MAX_SIGNATURES && (
														<button
															type='button'
															className='btn btn-sm btn-light-primary'
															onClick={() => setSetting('signatureLabels', [...settings.signatureLabels, ''])}
														>
															Add signature
														</button>
													)}
												</div>
												{numberField('signatureGap', 'Space above', 'mm', 0, 80)}
											</div>
										)}
										<div className='mb-3'>
											<label className='form-label fs-8 mb-1' htmlFor='pd-footerNote'>
												Footer note <span className='text-muted'>(printed at the end of the document)</span>
											</label>
											<textarea
												id='pd-footerNote'
												className='form-control form-control-sm'
												rows={2}
												maxLength={1000}
												value={settings.footerNote}
												onChange={(e) => setSetting('footerNote', e.target.value)}
											/>
										</div>

										<div className='border-top pt-4 mt-5'>
											<label className='form-check form-check-custom form-check-solid form-check-sm mb-4'>
												<input
													className='form-check-input'
													type='checkbox'
													checked={applyToAll}
													onChange={(e) => setApplyToAll(e.target.checked)}
												/>
												<span className='form-check-label'>
													Also apply the page, text and table settings to all documents of this project
												</span>
											</label>
											<div className='d-flex flex-wrap gap-2'>
												<button type='submit' className='btn btn-sm btn-success' disabled={busy}>
													Save {activeDocument?.label}
												</button>
												<button type='button' className='btn btn-sm btn-light-primary' onClick={() => handlePreviewPdf(false)}>
													Preview PDF
												</button>
												{!singleLine && (
													<button type='button' className='btn btn-sm btn-light-primary' onClick={() => handlePreviewPdf(true)}>
														Preview 2 pages
													</button>
												)}
												{activeDocument?.isCustomized && (
													<button type='button' className='btn btn-sm btn-light ms-auto' onClick={handleReset}>
														Reset to default
													</button>
												)}
											</div>
											{isDirty && <div className='text-danger fs-8 mt-2'>{activeDocument?.label} has unsaved changes.</div>}
										</div>
									</form>
								</div>

								<div className='col-lg-5 mt-8 mt-lg-0'>
									<div style={{ position: 'sticky', top: 90 }}>
										<div className='d-flex justify-content-between align-items-center mb-2'>
											<span className='fw-bolder'>{activeDocument?.label} preview</span>
											<label className='form-check form-check-custom form-check-solid form-check-sm mb-0'>
												<input
													className='form-check-input'
													type='checkbox'
													checked={showGuides}
													onChange={(e) => setShowGuides(e.target.checked)}
												/>
												<span className='form-check-label fs-8'>Margin guide</span>
											</label>
										</div>
										{renderPreview()}
										<div className='text-muted fs-8 mt-2'>
											Sample data. Use Preview PDF to see the exact print.
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			{hasPermission === 0 && <Error />}
		</>
	)
}

export default PrintDesigns
